import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.museshaircwb.com';

const schemaJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "HairSalon",
      "@id": `${SITE_URL}/#hairsalon`,
      "name": "Muses Hair CWB",
      "url": SITE_URL,
      "logo": "https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png",
      "image": "https://www.museshaircwb.com/wp-content/uploads/2024/01/Muses-Hair-Salon-Causeway-Bay-Hong-Kong-scaled.jpg",
      "description": "Professional solution center for hair and scalp problems. Expert hair stylist with 20+ years experience and certified scalp therapist serving international clients in Causeway Bay, Hong Kong.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Causeway Bay",
        "addressRegion": "Hong Kong",
        "addressCountry": "HK"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 22.2800,
        "longitude": 114.1840
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
          "opens": "10:00",
          "closes": "20:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Saturday","Sunday"],
          "opens": "10:00",
          "closes": "19:00"
        }
      ],
      "priceRange": "HK$580 – HK$880",
      "currenciesAccepted": "HKD",
      "paymentAccepted": "Cash, Credit Card",
      "sameAs": [
        "https://www.instagram.com/museshaircwb",
        "https://www.facebook.com/museshaircwb"
      ]
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": SITE_URL,
      "name": "Muses Hair CWB",
      "publisher": { "@id": `${SITE_URL}/#hairsalon` }
    },
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      "url": SITE_URL,
      "name": "Muses Hair CWB | Professional Hair & Scalp Problem Solutions Causeway Bay Hong Kong",
      "description": "Professional solution center for hair and scalp problems. 20+ years experience serving international clients. Certified scalp therapist in Causeway Bay, Hong Kong.",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#hairsalon` },
      "inLanguage": "en",
      "breadcrumb": {
        "@id": `${SITE_URL}/#breadcrumb`
      }
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        }
      ]
    }
  ]
};

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const scriptId = 'schema-jsonld-home';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemaJsonLd);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#F3F0EB]/98 backdrop-blur-sm shadow-sm' : 'bg-[#F3F0EB]'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
              alt="Muses Hair CWB" 
              className="h-11 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Home</Link>
            <Link to="/about" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">About</Link>
            <Link to="/qualifications" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Qualifications</Link>
            <Link to="/services" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Services</Link>
            <Link to="/products" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Products</Link>
            <Link to="/testimonials" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Testimonials</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Contact</Link>
          </div>
          <button onClick={scrollToContact} className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
            Book Consultation
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 bg-[#F3F0EB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
                Professional Hair & Scalp Solutions
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Relax, Refresh,<br />Recharge
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block"></span>
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">AI Assistant 24/7</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/72c9431bc3a97522c7978b0a7d906738/e3f4ced8a3ee82718628ff87bdb7a655.jpeg" 
                  alt="Professional Hair Salon Interior" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems We Solve Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              We Solve Real Hair & Scalp Problems
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              If you're experiencing any of these concerns, you're in the right place
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#F9F8F6] rounded-lg p-8 hover:shadow-md transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6">
                <i className="ri-contrast-drop-2-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Hair Thinning</h3>
              <p className="text-gray-600 leading-relaxed">
                Professional scalp analysis and targeted treatments to address hair loss and promote healthy hair growth from the root.
              </p>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 hover:shadow-md transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6">
                <i className="ri-water-flash-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Oily Scalp</h3>
              <p className="text-gray-600 leading-relaxed">
                Specialized scalp therapy to balance oil production, reduce greasiness, and maintain a healthy scalp environment.
              </p>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 hover:shadow-md transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6">
                <i className="ri-snowflake-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Dandruff</h3>
              <p className="text-gray-600 leading-relaxed">
                Effective treatments to eliminate flaking, soothe irritation, and restore scalp health with professional-grade solutions.
              </p>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 hover:shadow-md transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6">
                <i className="ri-fire-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Damaged Hair</h3>
              <p className="text-gray-600 leading-relaxed">
                Deep repair treatments using premium products to restore strength, elasticity, and shine to chemically or heat-damaged hair.
              </p>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 hover:shadow-md transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6">
                <i className="ri-windy-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Frizzy Hair</h3>
              <p className="text-gray-600 leading-relaxed">
                Smoothing treatments and expert techniques to tame unruly hair, control frizz, and achieve sleek, manageable results.
              </p>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 hover:shadow-md transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6">
                <i className="ri-heart-pulse-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Scalp Sensitivity</h3>
              <p className="text-gray-600 leading-relaxed">
                Gentle, therapeutic care for sensitive or irritated scalps using hypoallergenic products and soothing techniques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hair Spa Process Section */}
      <section className="py-20 px-6 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-6">
              Signature Treatment
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              The Hair Spa Experience
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A luxurious step-by-step ritual designed to deeply nourish, restore, and revive your hair and scalp
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-0">
              {[
                {
                  step: '01',
                  icon: 'ri-search-eye-line',
                  title: 'Scalp Consultation & Analysis',
                  desc: 'We begin with a thorough scalp and hair assessment to understand your unique condition — checking moisture levels, scalp health, and hair porosity to tailor the treatment specifically for you.',
                },
                {
                  step: '02',
                  icon: 'ri-drop-line',
                  title: 'Pre-Treatment Oil Massage',
                  desc: 'Warm nourishing oils are gently massaged into the scalp and hair lengths. This stimulates blood circulation, loosens buildup, and prepares the hair to absorb the treatment deeply.',
                },
                {
                  step: '03',
                  icon: 'ri-bubble-chart-line',
                  title: 'Clarifying Shampoo Cleanse',
                  desc: 'A professional-grade clarifying shampoo removes excess oil, product buildup, and impurities — creating a clean canvas so the treatment can penetrate every strand effectively.',
                },
                {
                  step: '04',
                  icon: 'ri-leaf-line',
                  title: 'Deep Conditioning Mask',
                  desc: 'A rich, protein-infused hair mask is applied from roots to tips. Packed with keratin, vitamins, and botanical extracts, it repairs damage, restores elasticity, and locks in moisture.',
                },
                {
                  step: '05',
                  icon: 'ri-temp-hot-line',
                  title: 'Steam Therapy',
                  desc: 'Gentle steam opens the hair cuticle, allowing the mask to penetrate deep into the cortex. This is the key step that transforms a regular treatment into a true spa-level experience.',
                },
                {
                  step: '06',
                  icon: 'ri-sparkling-2-line',
                  title: 'Rinse, Tone & Finish',
                  desc: 'The mask is rinsed with cool water to seal the cuticle and lock in shine. A finishing serum is applied to smooth the surface, leaving hair silky, luminous, and beautifully manageable.',
                },
              ].map((item, index) => (
                <div key={index} className="flex gap-6 group">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full shrink-0 group-hover:bg-gray-700 transition-colors">
                      <i className={`${item.icon} text-white text-lg`}></i>
                    </div>
                    {index < 5 && <div className="w-px flex-1 bg-gray-200 my-2"></div>}
                  </div>
                  <div className="pb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{item.step}</span>
                      <h3 className="font-serif text-xl text-gray-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.title}</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/6912b5fddf01806bccd3f9f734256e43.jpg"
                  alt="Hair Spa Treatment Process"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-5 shadow-md">
                <p className="font-serif text-lg text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Treatment Duration
                </p>
                <p className="text-gray-600 text-sm">Approximately <strong>60 – 90 minutes</strong> for a full Hair Spa session, tailored to your hair's needs.</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-heart-line', label: 'Deeply Nourishing', desc: 'Restores moisture and nutrients lost from heat, colour, or environmental stress.' },
              { icon: 'ri-shield-check-line', label: 'Scalp Protective', desc: 'Balances the scalp microbiome and reduces sensitivity, flaking, and irritation.' },
              { icon: 'ri-sparkling-line', label: 'Instant Shine', desc: 'Seals the cuticle for mirror-like shine and long-lasting smoothness after just one session.' },
            ].map((benefit, i) => (
              <div key={i} className="bg-white rounded-lg p-6 flex items-start gap-4 shadow-sm">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full shrink-0">
                  <i className={`${benefit.icon} text-gray-700 text-lg`}></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{benefit.label}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=close%20up%20of%20healthy%20beautiful%20hair%20and%20scalp%20being%20examined%20by%20professional%20hands%2C%20natural%20lighting%2C%20clean%20minimal%20aesthetic%2C%20premium%20hair%20care%20concept%2C%20soft%20focus%20background%2C%20warm%20neutral%20tones%2C%20professional%20beauty%20treatment%20setting%2C%20serene%20calming%20mood&width=600&height=750&seq=philosophy-01&orientation=portrait" 
                  alt="Professional Scalp Analysis" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
                Our Philosophy
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Hair Beauty Starts<br />From Scalp Health
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                True hair beauty cannot exist without a healthy scalp foundation. This is the core principle that guides everything we do at Muses Hair CWB.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                With over 20 years of experience serving international clients and expats, combined with advanced scalp therapy certification, I bring a unique dual expertise to every consultation.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Whether you're seeking a precision haircut, transformative color, or solutions to persistent scalp concerns, you'll receive personalized care rooted in professional knowledge and genuine understanding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dual Expertise Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Dual Professional Expertise
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A rare combination of artistic styling mastery and clinical scalp therapy knowledge
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F9F8F6] rounded-lg p-10">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mb-6">
                <i className="ri-scissors-cut-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Expert Hair Stylist
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">20+ years of professional styling experience</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Specialized in serving international clients and expats</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Expert in cutting, coloring, and styling techniques</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Deep understanding of diverse hair types and textures</span>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Decades of hands-on experience working with clients from around the world, understanding the unique needs of different hair types, cultural preferences, and lifestyle requirements.
              </p>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-10">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mb-6">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Certified Scalp Therapist
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Professional scalp therapy certification</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Advanced training in scalp health and treatment</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Expertise in diagnosing scalp conditions</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Specialized therapeutic treatment protocols</span>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Clinical knowledge and therapeutic techniques to address scalp concerns at their root cause, providing effective solutions for conditions that affect hair health and growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* International Clientele Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
            Trusted by International Clients
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Serving Expats & International<br />Clients in Hong Kong
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto mb-12">
            With two decades of experience working with clients from diverse cultural backgrounds, I understand the unique hair care needs of the international community. From European fine hair to Asian thick hair, from textured curls to straight styles — every client receives personalized care tailored to their individual needs.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">20+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">1000+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">30+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider">Countries Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">100%</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider">Professional Care</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Ready to Solve Your<br />Hair & Scalp Concerns?
          </h2>
          <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
            Book a professional consultation to discuss your specific needs and discover the right solution for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://wa.me/message/YAZZ2YL77SHLN1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-3.5 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp Consultation
            </a>
            <button className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3.5 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer">
              Book Appointment
            </button>
          </div>
          <p className="text-gray-500 text-sm mt-6">
            Located in Causeway Bay, Hong Kong • Serving local and international clients
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional solution center for hair and scalp problems in Causeway Bay, Hong Kong.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">About</Link></li>
                <li><Link to="/qualifications" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Qualifications</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Services</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">More</h4>
              <ul className="space-y-3">
                <li><Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Products</Link></li>
                <li><Link to="/testimonials" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Testimonials</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Connect</h4>
              <ul className="space-y-3">
                <li><a href="https://www.instagram.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Instagram</a></li>
                <li><a href="https://www.facebook.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Facebook</a></li>
                <li><a href="https://wa.me/message/YAZZ2YL77SHLN1" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">WhatsApp</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 Muses Hair CWB. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}